/**
 * to navigate through application routes use
 * @example: app.navigate('landingpage', {optional} options);
 */
import app from 'app';
import Marionette from 'backbone.marionette';
import Invite from '../views/invitations/Invite.vue';
import wtSettings from 'components/modals/types/wtSettings.component.vue';
import { AspectRatiosController } from 'c/AspectRatiosController';
import ProducedPopup from 'v/Produced/ProducedPopup.vue';
import StoryboardPopup from 'v/Dashboard/StoryboardPopup.vue';

app.namespace.Controller.AppController = Marionette.Controller.extend({
  initialize: function () {
    this.appView = new app.namespace.View.AppLayout();

    this.listenTo(
      app,
      'app.dashboard',
      function () {
        if (this.isStoryPage()) {
          app.storyController
            .exit({ type: 'exit', navigationOverride: true })
            .done(function () {
              app.navigateToDashboard();
            });
        } else {
          app.navigateToDashboard();
        }
      },
      this
    );

    this.listenTo(
      app,
      'app.dashboard.myVideos',
      function () {
        if (this.isStoryPage()) {
          app.storyController
            .exit({ type: 'exit', navigationOverride: true })
            .done(function () {
              app.navigate('dashboard/myVideos');
            });
        } else {
          app.navigate('dashboard/myVideos');
        }
      },
      this
    );

    this.listenTo(
      app,
      'app.dashboard.inProgress',
      function () {
        if (this.isStoryPage()) {
          this.exitStory();
        } else {
          app.navigate('dashboard/inProgress');
        }
      },
      this
    );

    this.listenTo(
      app,
      'app.dashboard.invite',
      function () {
        app.analytics('analytics:invitations:clickOnAvatar');
        app.showModal({
          type: 'custom',
          component: Invite,
          showCloseButton: false,
          closeOnEscape: false,
          wrapperClass: [],
          store: app.rootStore,
          componentPropsData: {},
        });
      },
      this
    );

    this.listenTo(
      app,
      'app.dashboard.settings',
      function () {
        app.showModal({
          type: 'big',
          component: wtSettings,
          wrapperClass: ['wt-setting-modal'],
        });
      },
      this
    );

    this.listenTo(
      app,
      'app.logout',
      function () {
        this.saveAndExitOnLogout();
      },
      this
    );

    this.listenTo(app, 'app.story.export', () => {
      app.importExportController.export();
    });
    this.listenTo(app, 'app.story.import', () => {
      app.importExportController.import();
    });

    this.listenTo(app, 'app.remoteStash.clear', () => {
      app.dialog
        .confirm({
          type: app.dialog.types.CONFIRM_3,
          title: 'Are you sure you want to clear the remote stash?',
          description: "There's no way back...",
          buttonYesText: 'Clear',
        })
        .then((data) => {
          if (data.action) {
            let storyId;
            if (app.storyController.story) {
              storyId = app.storyController.story.get('id');
              app.storyController.story.remoteStash.clear({ force: true });
            }
            app.remoteStash.clear({ force: true });
            let description = storyId
              ? `Also cleared remote stash for story #${storyId}.<br>`
              : '';
            description += "It's recommended to refresh the browser's tab.";
            app.dialog.confirm({
              type: app.dialog.types.CONFIRM_1,
              title: 'Remote Stash Cleared',
              description,
              buttonYesText: 'OK',
            });
          }
        });
    });

    this.listenTo(
      app,
      'app.exit',
      _.debounce(
        function () {
          this.exitStory();
        },
        app.TIME.MENTAL.λ,
        { leading: true, trailing: false }
      ),
      this
    );

    app.notificationController.subscribe(
      () => {
        app.notificationController.push({
          text: `
        This story is transferred from the previous UI flow to the new one.
        <br/>As a result, you may experience inaccuracies on the subtitles.
        <br/>If you want the captions to be on perfect timing, you can upload the recordings again.
        `,
          type: 'WARN',
          timeout: app.TIME.second(15),
          uniqueGroupName: 'STORY_MIGRATED_TO_NEW_VO',
        });
      },
      {
        events: ['STORY_MIGRATED_TO_NEW_VO'],
      }
    );
  },

  exitStory(options) {
    options = Object.assign(
      {
        navigationOverride: true,
        navigateTo: 'dashboard/inProgress',
      },
      options
    );

    app.storyController
      .exit({
        type: 'exit',
        navigationOverride: options.navigationOverride,
      })
      .done(function () {
        app.state.enableNavigation();
        app.navigate('dashboard/inProgress');
      });
  },

  isStoryPage: function () {
    const url = document.location.href;
    return url.indexOf('stories') >= 0;
  },

  showViewInModal: function (view) {
    this.appView.showModal(view);
  },

  hideModal: function () {
    this.appView.hideModal();
  },

  showNotification: function (view) {
    this.appView.rNotificationBar.show(view);
  },
  hideNotification: function () {
    this.appView.rNotificationBar.empty();
  },

  /**
   * when route is not chosen...
   */
  routeDefault: function () {
    if (app.rootStore.getters['user/isImpededTemplatizer']) {
      const firstTab =
        app.rootStore.getters['masthead/enabledNavigationTabs'][0];
      if (firstTab) {
        app.navigate(firstTab.path);
      } else {
        app.navigate('404');
      }
    } else {
      app.navigate('dashboard/home');
    }
  },

  routeDashboard: function (tab, params) {
    const path = `dashboard/${tab}`;
    const hasTab = app.rootStore.getters['masthead/enabledNavigationTabs'].find(
      (tab) => tab.path === path
    );

    if (hasTab) {
      let categoryId;
      let q;
      if (params) {
        const pairs = params.split('&') || [];
        for (const pair of pairs) {
          const [key, value] = pair.split('=');
          if (key === 'categoryId') {
            categoryId = value;
          }
          if (key === 'q') {
            q = value;
          }
        }
      }
      this.appView.showDashboard({ categoryId, q });
    } else {
      const firstTab =
        app.rootStore.getters['masthead/enabledNavigationTabs'][0];
      if (firstTab) {
        app.navigate(firstTab.path);
      } else {
        app.navigate('404');
      }
    }
  },

  // Full-page version history for a story family. `id` is any group member;
  // the endpoint resolves the whole group. The panel reads the id from the
  // route path (state.routePath) once mounted.
  routeStoryVersions: function () {
    this.appView.showDashboard({});
  },

  _canStartStory: function (id) {
    const canStartStory = new $.Deferred();

    if (app.rootStore.getters['user/box'].isDev()) {
      canStartStory.resolve();
    } else {
      app.broadcast.emit('sw-story-has', { storyId: id }).then((result) => {
        if (result) {
          app.broadcast.emit('bc-story-animate', { storyId: id });
          app.broadcast.accountOpenedStories();
          // already opened
          canStartStory.reject();
          app.dialog
            .error({
              type: app.dialog.types.ERROR_1,
              description:
                'This story is open in another tab. Please continue from there',
              title: 'Story In Use',
              showContactCsm: false,
            })
            .then(() => {
              app.navigateToDashboard();
            });
        } else {
          canStartStory.resolve();
        }
      });
    }

    return canStartStory;
  },

  routeStories: function (id, options) {
    options = $.extend(
      {
        storyOptions: null,
      },
      options
    );

    app.rootStore.commit('onRouteChanged', Backbone.history.fragment);
    this.storyPromise = new $.Deferred();
    app.delayedSpinner.show({ delay: 0 });
    app.realTimePreviewController.resetCache();

    this._canStartStory(id)
      .done(() => {
        this.initTinyMCE()
          .done(() => {
            this._startStory(id, options.storyOptions)
              .done(({ storyModel, remoteStash }) => {
                return app.storyController
                  .initStory({
                    story: storyModel,
                    remoteStash,
                    storyOptions: options.storyOptions,
                  })
                  .then(() => {
                    this.appView.showStory(storyModel, app.storyController);
                    this.storyPromise.resolve();
                    app.delayedSpinner.hide({ delay: 0 });
                    app.state.blockNavigation();
                  });
              })
              .fail((response) => {
                app.delayedSpinner.hide({ delay: 0 });
                if (401 !== response?.errorCode) {
                  this.showServerError(response);
                }
                this.storyPromise.reject();
              });
          })
          .fail(() => {
            app.delayedSpinner.hide({ delay: 0 });
            app.dialog
              .error({
                type: app.dialog.types.ERROR_1,
                title: app.culture.alert.TINYMCE_LOAD_ERROR,
                showContactCsm: true,
              })
              .then(() => {
                this.storyPromise.reject();
                app.navigateToDashboard({ replace: true });
              });
          });
      })
      .fail(() => {
        app.delayedSpinner.hide();
      });
  },

  initTinyMCE: function () {
    const tinyMcePromise = new $.Deferred();

    if (typeof window.tinyMCE === 'undefined') {
      const tinyMceScript = document.createElement('script');
      tinyMceScript.setAttribute(
        'src',
        `/resources/js/libs/tinymce/tinymce.js?${window.serverProfile.version}`
      );
      tinyMceScript.onload = function () {
        tinyMcePromise.resolve();
      };
      tinyMceScript.onerror = function () {
        tinyMcePromise.reject();
      };
      document.head.appendChild(tinyMceScript);
    } else {
      tinyMcePromise.resolve();
    }

    return tinyMcePromise;
  },

  routeReadyMade: function () {
    app.navigate('dashboard/discover');
  },

  routeVideoProducing: function (storyId) {
    app.delayedSpinner.show({ delay: 0 });

    const promise = new _.ColdPromise();

    app.api.draftStoryPreviews.get(storyId).then(
      (res) => {
        promise.resolve(res);
      },
      () => {
        // @note: probably already produced
        app.navigate(`/videos/${storyId}`);
      }
    );

    promise
      .then((storyPreview) => {
        const previewModel = new app.namespace.Model.StoryModel(storyPreview, {
          parse: true,
          editable: false,
        });

        if (previewModel.get('template')) {
          app.api.draftStoryTemplates
            .categoriesForStory(storyId)
            .then(({ categoryIds }) => {
              previewModel.set({
                categoryNames: this._categoryIdsToNames(categoryIds),
              });

              this.appView.showVideoProducing(
                previewModel,
                app.videoController
              );
            });
        } else {
          this.appView.showVideoProducing(previewModel, app.videoController);
        }
      })
      .catch(this.showServerError)
      .finally(app.delayedSpinner.hide);
  },

  routeVideos: function (id) {
    app.delayedSpinner.show({ delay: 0 });
    app.api.producedStoryPreviews
      .get(id)
      .then((story) => {
        if (story.template) {
          return app.api.draftStoryTemplates
            .categoriesForStory(id)
            .then(({ categoryIds }) => {
              story.categoryNames = this._categoryIdsToNames(categoryIds);
              this.appView.showVideo(story, app.videoController);
            });
        } else {
          this.appView.showVideo(story, app.videoController);
        }
      })
      .catch(this.showServerError)
      .finally(app.delayedSpinner.hide);
  },

  openProducedStoryModal(storyId) {
    app.closeModals(['unique-produced-story-modal', 'fullscreen']);

    Promise.all([
      app.api.producedStoryPreviews.get(storyId),
      app.api.draftStoryTemplates.categoriesForStory(storyId),
    ]).then((responses) => {
      const [story, meta] = responses;
      if (!_.isEmpty(meta.categoryIds)) {
        story.categoryNames = this._categoryIdsToNames(meta.categoryIds);
      }

      app.showModal({
        store: app.rootStore,
        type: 'fullscreen',
        component: story.template ? StoryboardPopup : ProducedPopup,
        componentPropsData: {
          video: story,
        },
        showCloseButton: false,
        wrapperClass: ['unique-produced-story-modal'],
      });
    });
  },

  /**
   * @param categoryIds {string[]|null}
   * @returns {string[]}
   * @private
   */
  _categoryIdsToNames(categoryIds) {
    categoryIds ??= [];
    return categoryIds.map((id) => {
      const o = app.rootStore.getters.categoryCommonwealth.find(
        (c) => c.id === id
      );
      return o ? o.categoryName : '';
    });
  },

  routeNotFound: function () {
    app.navigate('404', { trigger: false });
    app.delayedSpinner.hide();
    this.appView.showRouteNotFound();
  },

  _startStory: function (id, storyOptions) {
    storyOptions = $.extend(
      {
        parse: true,
        editable: true,
      },
      storyOptions
    );
    const deferred = new $.Deferred();

    const storyRemoteStash = new app.namespace.Model.RemoteStash({
      type: 'storyId',
      id,
      autosave: true,
    });
    storyRemoteStash.fetch();

    app.api.fonts.abort().clearCache();
    app.api.themeInfo.abort();
    app.api.draftStories
      .lockON(id)
      .then(() => {
        return app.api.draftStories.getStory(id);
      })
      .then(({ story, externalStoryEntryId }) => {
        if (_.isEmpty(app.programmers.get(story.programmerId))) {
          deferred.reject(app.culture.get('alert.UNKNOWN_PROGRAMMER'));
        } else if (
          !app.storyController.isStoryInPublishingStateByTaskType(
            story.taskType
          )
        ) {
          $.when(
            app.api.fonts.get({
              programmerId: story.programmerId,
              containDefaultFonts: true,
            }),
            app.api.themeInfo.get({
              programmerId: story.programmerId,
              brandId: story.brandId,
            }),
            app.api.textStyles.get(story.programmerId),
            story.template
              ? app.api.draftStoryTemplates.categoriesForStory(story.id)
              : Promise.resolve({})
          )
            .then(
              (
                fontsResponse,
                themeInfoResponse,
                textStyleResponse,
                templateResponse
              ) => {
                app.fontController =
                  new app.namespace.Controller.FontController(fontsResponse);

                // ensure themeInfo has story aspectRatio
                let orphanAspectRatio = null;
                let newAspectRatio = null;
                const found = themeInfoResponse.themeInfo.find(
                  (theme) => theme.aspectRatio === story.aspectRatio
                );
                if (!found) {
                  const storyBrand = app.rootStore.getters[
                    'user/selectedOrganization'
                  ].brands.find((brand) => brand.brandId === story.brandId);
                  newAspectRatio =
                    AspectRatiosController.getPrioritizedAspectRatio(
                      storyBrand.themes
                    ).aspectRatio;
                  orphanAspectRatio = story.aspectRatio;
                  // set valid AR for the time being of Story initialization flow
                  story.aspectRatio = newAspectRatio;
                }

                let templateExtra = {};
                if (story.template) {
                  if (!app.rootStore.getters['user/isTemplateSpecialist']) {
                    templateResponse.shortcutEnabled = true;
                    templateResponse.massProduceEnabled = false;
                  }
                  templateExtra = {
                    _categoryIds: templateResponse.categoryIds,
                    _shortcutEnabled: templateResponse.shortcutEnabled,
                    _massProduceEnabled: templateResponse.massProduceEnabled,
                    _lockBgMusic: templateResponse.lockBgMusic || false,
                  };
                }

                const storyModel = new app.namespace.Model.StoryModel(
                  {
                    ...story,
                    _themeInfo: themeInfoResponse.themeInfo,
                    _textStyles: textStyleResponse,
                    _externalStoryEntryId: externalStoryEntryId,
                    _rawFonts: fontsResponse,
                    ...templateExtra,
                    ...(storyOptions.attributes || {}),
                  },
                  storyOptions
                );

                storyModel.getLayoutsCustomOverlays();

                if (orphanAspectRatio) {
                  this.storyPromise.then(() => {
                    storyModel.set(
                      {
                        aspectRatio: orphanAspectRatio,
                        _aspectRatio: app.ASPECT_RATIOS[orphanAspectRatio],
                      },
                      { silent: true }
                    );
                    storyModel.aspectRatiosController
                      .setNewAspectRatio(newAspectRatio)
                      .then(
                        () => {
                          app.storyController.save();
                        },
                        () => {
                          app.storyController.exit();
                        }
                      );
                  });
                }

                return { storyModel };
              }
            )
            .then(({ storyModel }) => {
              deferred.resolve({ storyModel, remoteStash: storyRemoteStash });
            })
            .catch(deferred.reject);
        }
      })
      .catch(deferred.reject);

    return deferred.promise();
  },

  saveAndExitOnLogout: function () {
    if (this.isStoryPage()) {
      app.storyController.exit({ type: 'logout' });
    } else {
      app.rootStore.dispatch('user/logout');
    }
  },

  showServerError: function (error) {
    app.dialog.serverError(error).then(() => {
      app.navigateToDashboard({ replace: true });
    });
  },
});

export default app.namespace.Controller.AppController;
